import { cn } from '@/app/lib/utils';
import CustomSelect, { Option, CustomSelectProps } from '../ui/CustomSelect';
import { useEffect, useMemo, useState } from 'react';
import { useParams } from 'next/navigation';
import { useMutation, useQuery } from '@tanstack/react-query';
import { getCampaigns, getCampaign } from '@/api/campaign';
import Icon from '../ui/Icon';

type Props = {
  defaultValue: any;
  label?: string;
  required?: boolean;
  wrapperClassName?: string;
  className?: string;
  searchable?: boolean;
  clearable?: boolean;
  disabled?: boolean;
  placeholder?: string;
  multiple?: boolean;
  onOptionSelect: (option: any) => void;
  fetchSelectedOptionOnMount?: boolean;
  excludeId?: string;
} & Partial<CustomSelectProps>;

const CampaignSelect = ({
  defaultValue,
  label,
  required,
  wrapperClassName,
  className,
  searchable,
  clearable,
  disabled,
  placeholder,
  onOptionSelect,
  fetchSelectedOptionOnMount,
  multiple,
  excludeId,
  ...props
}: Props) => {
  const [search, setSearch] = useState('');
  const [selectedOption, setSelectedOption] = useState<Option | null>(null);
  const [selectedOptions, setSelectedOptions] = useState<Option[]>([]);
  const [isOptionCbTriggered, setIsOptionCbTriggered] = useState(false);
  const { clientId } = useParams<{ clientId: string }>();
  const { data: options, isLoading } = useQuery({
    queryKey: ['campaigns', clientId, search],
    queryFn: () =>
      getCampaigns({
        clientId: clientId || '',
        search: search,
      }),
    enabled: !!clientId,
  });
  const {
    mutate: getCampaignMutation,
    data: missingCampaign,
    isPending: isLoadingMissingCampaign,
  } = useMutation({
    mutationFn: (id: string) => getCampaign(clientId || '', id),
    onError: error => {
      console.error('error', error);
    },
  });

  const optionsList = useMemo(() => {
    const list: Option[] =
      options?.data
        ?.filter(option => !excludeId || option.id !== excludeId)
        .map(option => ({
          id: option.id,
          value: option?.name || '-',
        })) || [];
    if (!multiple && selectedOption && list.find(o => o.id === selectedOption.id) === undefined) {
      list.unshift({
        id: selectedOption.id.toString(),
        value: selectedOption.value.toString(),
      });
    }
    if (multiple && selectedOptions) {
      // find missing options
      const missingOptions = selectedOptions.filter(o => !list.some(c => c.id === o.id));
      list.unshift(...missingOptions);
    }
    if (missingCampaign) {
      list.unshift({
        id: missingCampaign.id,
        value: missingCampaign.name || '-',
      });
    }
    return list;
  }, [options, selectedOption, selectedOptions, multiple, missingCampaign]);

  useEffect(() => {
    if (fetchSelectedOptionOnMount && defaultValue && !isOptionCbTriggered && !isLoading) {
      let values: Option | Option[] | null = null;
      if (multiple) {
        values = optionsList.filter(o => defaultValue.includes(o.id)) || [];
      } else {
        values = optionsList.find(o => o.id === defaultValue) || null;
      }
      if ((Array.isArray(values) && values.length !== 0) || values !== null) {
        onOptionSelect(values);
        setIsOptionCbTriggered(true);
      } else if (defaultValue || (Array.isArray(defaultValue) && defaultValue.length !== 0)) {
        // call api
        getCampaignMutation(defaultValue);
      }
    }
  }, [
    fetchSelectedOptionOnMount,
    defaultValue,
    optionsList,
    onOptionSelect,
    isOptionCbTriggered,
    isLoading,
    multiple,
    getCampaignMutation,
  ]);

  return (
    <div className={cn('flex flex-col', wrapperClassName)}>
      {label && (
        <label className="text-sm font-medium mb-1.5 flex items-center gap-1 text-muted-foreground">
          <span>
            {label}
            {required && <span className="text-red-600 ml-1">*</span>}
          </span>
          {isLoadingMissingCampaign && (
            <Icon
              name="loader-2-fill"
              size={4}
              aria-label="Loading"
              className="animate-spin w-4 h-4"
            />
          )}
        </label>
      )}
      <CustomSelect
        options={optionsList || []}
        placeholder={placeholder}
        defaultValue={defaultValue}
        onOptionSelect={option => {
          if (multiple) {
            setSelectedOptions(option as Option[]);
          } else {
            setSelectedOption(option as Option);
          }
          onOptionSelect(option);
        }}
        searchable={searchable}
        clearable={clearable}
        disabled={disabled}
        triggerClassNames={className}
        onSearch={search => {
          setSearch(search);
        }}
        searchLoading={isLoading}
        multiple={multiple}
        {...props}
      />
    </div>
  );
};

export default CampaignSelect;
